import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, RotateCcw, Share2, ExternalLink } from 'lucide-react';
import EstimateDisplay from '../components/EstimateDisplay';
import QuestionLoader from '../components/QuestionLoader';
import FinalEstimateLoader from '../components/FinalEstimateLoader';
import API_BASE_URL from '../config/api';
import { authService } from '../services/authService';
import { getInstantImages } from '../utils/api';

const QuestionFlow = () => {
  const [projectData, setProjectData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [questionNumber, setQuestionNumber] = useState(1);
  const [answers, setAnswers] = useState([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [costEstimate, setCostEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const [dynamicImages, setDynamicImages] = useState({});
  const [generatingImages, setGeneratingImages] = useState(new Set());
  const [questionHistory, setQuestionHistory] = useState([]);
  const [costHistory, setCostHistory] = useState([]);
  const [, setPreviousCostEstimate] = useState(null);
  const [error, setError] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  // Note: FastAPI backend manages conversation state server-side

  // Share functionality
  const handleShare = () => {
    const costRange = costEstimate ? 
      `$${(costEstimate.min / 1000000).toFixed(2)}M - $${(costEstimate.max / 1000000).toFixed(2)}M` : 
      '$1.60M - $3.00M';
    
    if (navigator.share) {
      navigator.share({
        title: 'Construction Cost Estimate',
        text: `Get your construction cost estimate: ${costRange}`,
        url: window.location.href
      }).catch((error) => {
        // Handle user cancellation silently
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      });
    } else {
      // Fallback to copying to clipboard
      navigator.clipboard.writeText(window.location.href).then(() => {
        alert('Link copied to clipboard!');
      }).catch((error) => {
        console.error('Error copying to clipboard:', error);
      });
    }
  };
  const [userSelections, setUserSelections] = useState({
    hasBasement: false,
    luxuryFinish: false,
    premiumUpgrades: false
  });
  const navigate = useNavigate();

  // Function to analyze answers and detect user selections
  const analyzeUserSelections = (allAnswers) => {
    const selections = {
      hasBasement: false,
      luxuryFinish: false,
      premiumUpgrades: false
    };

    allAnswers.forEach(answer => {
      const answerText = answer.answer?.toLowerCase() || '';
      const questionText = answer.question?.toLowerCase() || '';

      // Detect basement selection
      if (answerText.includes('basement') || answerText.includes('full basement') || 
          answerText.includes('below-grade') || answerText.includes('below grade') ||
          (questionText.includes('foundation') && (answerText.includes('basement') || answerText.includes('crawl space with basement')))) {
        selections.hasBasement = true;
      }

      // Detect luxury finish selection
      if (answerText.includes('luxury') || answerText.includes('premium') || 
          answerText.includes('high-end') || answerText.includes('luxury premium') ||
          answerText.includes('custom finishes') || answerText.includes('designer') ||
          (questionText.includes('finish') && (answerText.includes('luxury') || answerText.includes('premium')))) {
        selections.luxuryFinish = true;
      }

      // Detect other premium upgrades
      if (answerText.includes('premium materials') || answerText.includes('upgraded') || 
          answerText.includes('high quality') || answerText.includes('custom') ||
          answerText.includes('smart home') || answerText.includes('energy efficient') ||
          answerText.includes('solar') || answerText.includes('high-efficiency')) {
        selections.premiumUpgrades = true;
      }
    });

    return selections;
  };

  // Helper function to format delta amounts
  const formatDeltaAmount = (amount) => {
    // Handle invalid/undefined amounts
    if (!amount || isNaN(amount) || amount <= 0) {
      return "+$0";
    }
    
    if (amount >= 1000000) {
      return `+${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `+${(amount / 1000).toFixed(0)}K`;
    } else {
      return `+$${amount.toFixed(0)}`;
    }
  };

  // Calculate base cost and deltas (reserved for EstimateDisplay or future use)
  // eslint-disable-next-line no-unused-vars
  const _calculateCostDeltas = () => {
    if (!costEstimate) return { 
      baseCost: 0, 
      luxuryCost: 0,
      additions: [], 
      totalDelta: 0, 
      isInitialState: true 
    };

    const currentCost = (costEstimate.min + costEstimate.max) / 2;
    const isFirstQuestion = questionNumber <= 1 && answers.length === 0;
    
    // For the first question, show Base vs Luxury models
    if (isFirstQuestion) {
      const baseCost = currentCost * 0.75; // 75% for base
      const luxuryCost = currentCost * 1.25; // 125% for luxury
      const delta = luxuryCost - baseCost;
      
      return {
        baseCost,
        luxuryCost,
        currentCost,
        totalDelta: delta,
        deltaPercentage: ((delta / baseCost) * 100),
        hasUpgrade: false,
        additions: [],
        isInitialState: true,
        showLuxuryComparison: true
      };
    }

    // After first question, calculate based on actual user selections
    const additionCosts = {
      basement: 200000,
      luxuryFinish: 340000,
      premiumUpgrades: 150000
    };

    let totalAdditions = 0;
    const activeAdditions = [];

    if (userSelections.hasBasement) {
      totalAdditions += additionCosts.basement;
      activeAdditions.push({
        name: 'Basement',
        cost: additionCosts.basement,
        delta: additionCosts.basement,
        deltaText: formatDeltaAmount(additionCosts.basement)
      });
    }

    if (userSelections.luxuryFinish) {
      totalAdditions += additionCosts.luxuryFinish;
      activeAdditions.push({
        name: 'Luxury Finish',
        cost: additionCosts.luxuryFinish,
        delta: additionCosts.luxuryFinish,
        deltaText: formatDeltaAmount(additionCosts.luxuryFinish)
      });
    }

    if (userSelections.premiumUpgrades) {
      totalAdditions += additionCosts.premiumUpgrades;
      activeAdditions.push({
        name: 'Premium Upgrades',
        cost: additionCosts.premiumUpgrades,
        delta: additionCosts.premiumUpgrades,
        deltaText: formatDeltaAmount(additionCosts.premiumUpgrades)
      });
    }

    // If no specific selections detected but we have answers, estimate based on progression
    if (totalAdditions === 0 && answers.length > 0) {
      // Calculate estimated additions based on current cost and question progression
      const progressionFactor = Math.min(answers.length / 10, 1); // 0 to 1 based on questions answered
      const estimatedAdditions = currentCost * (0.15 + progressionFactor * 0.15); // 15-30% based on progression
      totalAdditions = estimatedAdditions;
      
      activeAdditions.push({
        name: 'Selected Features',
        cost: estimatedAdditions,
        delta: estimatedAdditions,
        deltaText: formatDeltaAmount(estimatedAdditions)
      });
    }

    // Calculate base cost
    let baseCost = currentCost - totalAdditions;
    
    // Ensure there's always a reasonable difference
    if (totalAdditions < 200000 && answers.length > 0) {
      totalAdditions = Math.max(200000, currentCost * 0.2);
      baseCost = currentCost - totalAdditions;
      
      // Update additions if we adjusted the amount
      if (activeAdditions.length === 0 || activeAdditions[0].delta < 200000) {
        activeAdditions.length = 0; // Clear existing
        activeAdditions.push({
          name: 'Premium Features',
          cost: totalAdditions,
          delta: totalAdditions,
          deltaText: formatDeltaAmount(totalAdditions)
        });
      }
    }

    // Ensure base cost is reasonable
    const finalBaseCost = Math.max(baseCost, currentCost * 0.6);
    const finalDelta = totalAdditions;
    
    return {
      baseCost: finalBaseCost,
      totalAdditions,
      additions: activeAdditions,
      currentCost,
      totalDelta: finalDelta,
      deltaPercentage: finalBaseCost > 0 ? ((finalDelta / finalBaseCost) * 100) : 0,
      hasUpgrade: finalDelta > 50000,
      isInitialState: false,
      showLuxuryComparison: false
    };
  };

  // Update user selections when answers change
  useEffect(() => {
    if (answers.length > 0) {
      const newSelections = analyzeUserSelections(answers);
      setUserSelections(newSelections);
    }
  }, [answers]);

  useEffect(() => {
    const storedData = localStorage.getItem('projectData');
    if (!storedData) {
      navigate('/conceptual-estimate');
      return;
    }
    
    try {
      const data = JSON.parse(storedData);
      setProjectData(data);
      
      // Initialize the first question only if we haven't already
      if (!isInitialized && !currentQuestion) {
        initializeQuestions(data);
      } else {
      }
    } catch (error) {
      console.error('Error parsing project data:', error);
      navigate('/conceptual-estimate');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to load project data
  }, []);

  // Fetch cached images from Firebase Storage via backend API - COMPLETELY NON-BLOCKING
  // Images load asynchronously in background, UI never waits
  // ALSO triggers missing image generation ONCE per question (prevents API spam)
  useEffect(() => {
    if (!projectData || !currentQuestion) return;
    
    let pollInterval = null;
    let pollCount = 0;
    const maxPolls = 10; // Poll for up to 30 seconds (10 polls * 3 seconds) - Gemini takes 10-15s
    let missingGenerationTriggered = false; // Flag to ensure we only trigger generation ONCE
    
    // Fetch images immediately but don't block UI - wrapped in setTimeout to ensure it's async
    const fetchImages = async (isPolling = false) => {
      try {
        const options = {
          option_a: currentQuestion.option_a || '',
          option_b: currentQuestion.option_b || '',
          option_c: currentQuestion.option_c || '',
          option_d: currentQuestion.option_d || ''
        };
        
        // Count how many options we actually have (non-empty)
        const totalOptions = Object.values(options).filter(o => o && o.trim()).length;
        
        // Silently fetch images
        
        // Fetch images without timeout - this is non-blocking anyway
        const res = await getInstantImages({
          projectData: projectData,
          question: currentQuestion.question,
          options
        });
        
        if (!res.ok) {
          return false;
        }
        
        const data = await res.json();
        const images = data?.images || {};
        
        
        const updates = {};
        const missingOptions = {};
        
        // Check which options have images and which don't
        Object.keys(options).forEach(key => {
          const text = options[key];
          if (text) {
            const imageUrl = images[key];
            if (imageUrl && (imageUrl.startsWith('http') || imageUrl.startsWith('https'))) {
              const imageKey = `${currentQuestion.question}_${text}`;
              updates[imageKey] = imageUrl;
              if (isPolling) {
              } else {
              }
            } else {
              // Missing image - add to missingOptions
              missingOptions[key] = text;
            }
          }
        });
        
        // Log mismatch if we got fewer images than options
        if (Object.keys(images).length < totalOptions) {
        }
        
        // Update images if we got any
        if (Object.keys(updates).length > 0) {
          setDynamicImages(prev => {
            const newState = { ...prev, ...updates };
            return newState;
          });
          setForceUpdateKey(prev => prev + 1);
          
          // CRITICAL FIX: Only stop polling if we have ALL images
          // Don't return true yet - check for missing images first
        }
        
        // Check if we have ALL images now
        const allImagesFound = Object.keys(missingOptions).length === 0;
        
        if (allImagesFound) {
          // Stop polling if running
          if (pollInterval) {
            clearInterval(pollInterval);
            pollInterval = null;
          }
          return true; // All done!
        }
        
        // We have missing images - trigger generation ONCE
        if (!isPolling && !missingGenerationTriggered) {
          missingGenerationTriggered = true;
          
          // Call the backend API ONCE to generate all missing images
          fetch(`${API_BASE_URL}/api/generate-missing-images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectData: projectData,
              question: currentQuestion.question,
              options: missingOptions
            })
          }).then(res => res.json())
            .then(data => {
            })
            .catch(err => {
              console.error('❌ Failed to trigger missing image generation:', err);
            });
        }
        
        return false; // Still have missing images - keep polling
      } catch (e) {
        // Non-blocking - log error but don't break flow
        if (!isPolling) {
          console.error('❌ Image fetch error:', e);
        }
        return false;
      }
    };
    
    // Initial fetch - wrapped in setTimeout to ensure it doesn't block UI rendering
    setTimeout(() => {
      fetchImages(false).then(allImagesFound => {
        // If ANY images are missing, start polling for newly generated images
        if (!allImagesFound) {
          pollInterval = setInterval(() => {
            pollCount++;
            if (pollCount >= maxPolls) {
              clearInterval(pollInterval);
              pollInterval = null;
              return;
            }
            fetchImages(true).then(foundAllNewImages => {
              // Stop polling if we found all missing images
              if (foundAllNewImages && pollInterval) {
                clearInterval(pollInterval);
                pollInterval = null;
              }
            });
          }, 3000); // Poll every 3 seconds
        }
      }).catch(() => {
        // Silent error - images are optional
      });
    }, 0); // Execute on next tick to not block rendering
    
    // Cleanup polling on unmount or question change
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };
  }, [projectData, currentQuestion]);

  const initializeQuestions = async (data) => {
    if (isInitialized) {
      return;
    }
    setLoading(true);
    setIsInitialized(true); // Set flag immediately to prevent duplicates
    
    try {
      // Use FastAPI initialize-questions endpoint
      // Timeout is 60s - enough for OpenAI and image generation  
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT')), 60000)
      );
      
      const result = await Promise.race([
        authService.callAPI(async () => {
          const response = await authService.makeAPIRequest(`${API_BASE_URL}/api/initialize-questions`, {
          method: 'POST',
          body: JSON.stringify({
            constructionType: data.constructionType,
            city: data.city,
            state: data.state
          })
        });
        
          if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
          }
          
          return await response.json();
        }),
        timeoutPromise
      ]);
      
      // Successfully initialized
      
      // Set the question and cost estimate from FastAPI response IMMEDIATELY
      // Don't wait for images - they load in background
      setCurrentQuestion(result.question);
      setCostEstimate(result.costEstimate);
      setProjectData(data);
      setQuestionNumber(1);
      setAnswers([]);
      setSelectedAnswer('');
      setError(''); // Clear any previous errors
      setIsInitialized(true); // Mark as initialized
      
      // IMPORTANT: Set loading to false IMMEDIATELY so UI is responsive
      setLoading(false);
      
      // Trigger background image fetching (completely non-blocking)
      // Images will appear when ready - don't wait for them
      setTimeout(() => {
        if (result.question) {
          preGenerateQuestionImages(result.question);
        }
      }, 50); // Minimal delay
      
    } catch (error) {
      console.error('Error initializing questions:', error);
      
      // Don't show timeout errors to users - just retry silently
      if (error.message === 'TIMEOUT') {
        setIsInitialized(false); // Reset flag to allow retry
        setTimeout(() => {
          initializeQuestions(data);
        }, 1000);
        return;
      }
      
      // For real errors (not timeouts), show a friendly message
      setError('Unable to load questions. Please refresh the page.');
      setIsInitialized(false);
      setLoading(false);
    }
  };

  const reinitializeConversation = async (answersToReplay) => {
    try {
      // First, reinitialize with the base project data
      await initializeQuestions(projectData);
      
      // Then replay all the answers to get to the current state
      let currentQuestionNum = 1;
      let currentQuestionData = null;
      let currentCostData = null;
      
      for (let i = 0; i < answersToReplay.length; i++) {
        const answerData = answersToReplay[i];
        if (currentQuestionNum >= 10) {
          // If we've reached the final questions, break
          break;
        }
        
        const questionNumForApi = currentQuestionNum;
        const result = await authService.callAPI(async () => {
          const response = await authService.makeAPIRequest(`${API_BASE_URL}/api/next-question`, {
          method: 'POST',
          body: JSON.stringify({
            projectData: {
              constructionType: projectData.constructionType,
              city: projectData.city,
              state: projectData.state,
              timestamp: new Date().toISOString()
            },
            currentAnswer: answerData.answer,
            answers: answersToReplay.slice(0, questionNumForApi)
          })
        });
        
          if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
          }
          
          return await response.json();
        });
        
        currentQuestionData = result.question;
        currentCostData = result.costEstimate;
        currentQuestionNum++;
      }
      
      // Update the UI with the final state
      if (currentQuestionData && currentCostData) {
        setCurrentQuestion(currentQuestionData);
        setCostEstimate(currentCostData);
        setQuestionNumber(currentQuestionNum);
        setSelectedAnswer(''); // Clear the selected answer
        setForceUpdateKey(prev => prev + 1);
      }
      
    } catch (error) {
      console.error('Error reinitializing conversation:', error);
      setError('Failed to restore conversation state. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (answer) => {
    setSelectedAnswer(answer);
  };

  const handleNext = async () => {
    if (!selectedAnswer || loading) return; // Prevent duplicate calls when already loading


    // Store current question and cost in history before moving forward
    setQuestionHistory(prev => [...prev, currentQuestion]);
    setCostHistory(prev => [...prev, costEstimate]);

    const newAnswers = [...answers, {
      question: currentQuestion.question,
      answer: selectedAnswer,
      questionNumber
    }];
    setAnswers(newAnswers);

    setLoading(true);
    try {
      // Check if we've gone back and need to reinitialize the conversation
      // This happens when the current question number is less than or equal to the number of answers we had before
      const needsReinitialize = newAnswers.length < questionNumber;
      
      if (needsReinitialize) {
        // Reinitialize the conversation with all current answers
        await reinitializeConversation(newAnswers);
        return;
      }
      // If this is question 10 or later, directly get final estimate
      if (questionNumber >= 10) {
        
        const finalResult = await authService.callAPI(async () => {
          const finalResponse = await authService.makeAPIRequest(`${API_BASE_URL}/api/final-estimate`, {
            method: 'POST',
            body: JSON.stringify({
              projectData: {
                constructionType: projectData.constructionType,
                city: projectData.city,
                state: projectData.state,
                timestamp: new Date().toISOString()
              },
              answers: newAnswers
            })
          });
          
          if (!finalResponse.ok) {
            throw new Error(`Final estimate failed: ${finalResponse.status} ${finalResponse.statusText}`);
          }
          
          return await finalResponse.json();
        });
        
        // Store final data and navigate to cost estimate
        if (finalResult.costBreakdown) {
          localStorage.setItem('finalBreakdown', JSON.stringify(finalResult.costBreakdown));
        }
        if (finalResult.requirements) {
          localStorage.setItem('finalRequirements', JSON.stringify(finalResult.requirements));
        }
        localStorage.setItem('finalAnswers', JSON.stringify(newAnswers));
        localStorage.setItem('finalCostEstimate', JSON.stringify(costEstimate));
        navigate('/cost-estimate');
        return;
      }
      
      // Otherwise, get next question
      
      // Timeout is 60s - enough for OpenAI and image generation  
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('TIMEOUT')), 60000)
      );
      
      const result = await Promise.race([
        authService.callAPI(async () => {
          const response = await authService.makeAPIRequest(`${API_BASE_URL}/api/next-question`, {
          method: 'POST',
          body: JSON.stringify({
            projectData: {
              constructionType: projectData.constructionType,
              city: projectData.city,
              state: projectData.state,
              timestamp: new Date().toISOString()
            },
            currentAnswer: selectedAnswer,
            answers: newAnswers
          })
        });
        
          if (!response.ok) {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
          }
          
          return await response.json();
        }),
        timeoutPromise
      ]);
      
      // Check if no more questions available (shouldn't happen with our new logic)
      if (!result.question) {
        setError('No more questions available. Please contact support.');
        setLoading(false);
        return;
      }
      
      // Clear selected answer first
      setSelectedAnswer('');
      
      // Update all state immediately with forced re-renders
      const newQuestion = result.question;
      const newCost = result.costEstimate;
      const newQuestionNum = questionNumber + 1;
      
      // CRITICAL: Set loading to false IMMEDIATELY so UI is responsive
      setLoading(false);
      
      // Force state updates with keys to trigger re-renders
      setCurrentQuestion(() => {
        return { ...newQuestion, _forceUpdate: Date.now() };
      });
      
      setError(''); // Clear any previous errors
      
      // Trigger background image fetching (non-blocking, doesn't wait)
      // Images will load asynchronously and update when ready
      setTimeout(() => {
        preGenerateQuestionImages(newQuestion);
      }, 50); // Minimal delay
      
      // Store previous cost before updating
      setPreviousCostEstimate(costEstimate);
      
      setCostEstimate(() => {
        return { ...newCost, _forceUpdate: Date.now() };
      });
      
      setQuestionNumber(() => {
        return newQuestionNum;
      });

      // Force complete component re-render
      setForceUpdateKey(prev => prev + 1);
      
    } catch (error) {
      console.error('Error getting next question:', error);
      
      // Don't show timeout errors to users - just retry silently
      if (error.message === 'TIMEOUT') {
        setTimeout(() => {
          handleNext(selectedAnswer);
        }, 1000);
        return;
      } else {
        setError('Unable to load the next question. Please try again.');
      }
      
      setLoading(false); // CRITICAL: Always clear loading on error
    }
  };

  const handleBack = () => {
    if (questionNumber <= 1) {
      // Go back to the conceptual estimate form
      navigate('/conceptual-estimate');
      return;
    }

    // Get the previous question and cost from history
    const previousQuestion = questionHistory[questionHistory.length - 1];
    const previousCost = costHistory[costHistory.length - 1];
    
    // Get the previous answer
    const previousAnswer = answers[answers.length - 1]?.answer || '';

    // Update states to go back
    setCurrentQuestion(previousQuestion);
    setPreviousCostEstimate(costHistory[costHistory.length - 2] || null); // Get the cost before the previous one
    setCostEstimate(previousCost);
    setQuestionNumber(prev => prev - 1);
    setSelectedAnswer(previousAnswer);
    
    // Remove the last answer from answers array
    setAnswers(prev => prev.slice(0, -1));
    
    // Remove the last items from history
    setQuestionHistory(prev => prev.slice(0, -1));
    setCostHistory(prev => prev.slice(0, -1));
    
    // Force re-render
    setForceUpdateKey(prev => prev + 1);
  };

  const handleRetake = () => {
    localStorage.removeItem('projectData');
    localStorage.removeItem('finalAnswers');
    localStorage.removeItem('finalCostEstimate');
    navigate('/conceptual-estimate');
  };

  if (!projectData) {
    return <QuestionLoader />;
  }

  if (!currentQuestion && !loading) {
    // If no question and not loading, try to initialize
    const storedData = localStorage.getItem('projectData');
    if (storedData) {
      try {
        const data = JSON.parse(storedData);
        if (data && !isInitialized) {
          initializeQuestions(data);
        }
      } catch (e) {
        console.error('Error parsing stored project data:', e);
      }
    }
    return <QuestionLoader />;
  }

  if (!currentQuestion) {
    return <QuestionLoader />;
  }

  // Show FinalEstimateLoader when processing final estimate
  if (loading && questionNumber >= 10) {
    return <FinalEstimateLoader />;
  }

  const isNumericQuestion = !currentQuestion.option_a && !currentQuestion.option_b && 
                           !currentQuestion.option_c && !currentQuestion.option_d;

  // Function to trigger backend image generation (completely non-blocking). Reserved for future use.
  // eslint-disable-next-line no-unused-vars
  function _generateImageOnDemand(optionText, questionText = null) {
    const contextQuestion = questionText || currentQuestion?.question || 'unknown';
    const imageKey = `${contextQuestion}_${optionText}`;
    
    // Check if already generating or already exists
    if (generatingImages.has(imageKey) || dynamicImages[imageKey]) {
      return;
    }
    
    // Debounce: Don't generate same image multiple times
    const debounceKey = `generate_${imageKey}`;
    if (window[debounceKey]) {
      return;
    }
    window[debounceKey] = true;
    
    // Mark as generating (non-blocking)
    setGeneratingImages(prev => new Set([...prev, imageKey]));
    
    // Trigger backend generation in background (fire and forget)
    // Backend will generate and save to Firebase Storage
    fetch(`${API_BASE_URL}/api/generate-missing-images`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectData: projectData,
        question: contextQuestion,
        options: { option_a: optionText }
      })
    }).catch(err => {
      console.error(`Background image generation failed for "${optionText}":`, err);
    }).finally(() => {
      // Remove from generating set after a delay
      setTimeout(() => {
        setGeneratingImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(imageKey);
          return newSet;
        });
        delete window[debounceKey];
      }, 2000);
    });
  }

  // Function to pre-generate images for all options in a question (completely non-blocking)
  function preGenerateQuestionImages(question) {
    if (!question || !projectData) return;
    
    // Only generate if images are missing - don't spam the API
    const options = {};
    ['a', 'b', 'c', 'd'].forEach(option => {
      const optionText = question[`option_${option}`];
      if (optionText) {
        const imageKey = `${question.question}_${optionText}`;
        // Only generate if image doesn't exist
        if (!dynamicImages[imageKey]) {
          options[`option_${option}`] = optionText;
        }
      }
    });
    
    // Only trigger backend if there are missing images
    if (Object.keys(options).length > 0) {
      // Debounce: Only call once per question, not multiple times
      const debounceKey = `generate_${question.question}`;
      if (window[debounceKey]) {
        return; // Already generating for this question
      }
      window[debounceKey] = true;
      
      fetch(`${API_BASE_URL}/api/generate-missing-images`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectData: projectData,
          question: question.question,
          options: options
        })
      }).catch(err => {
        console.error('Background image generation failed:', err);
      }).finally(() => {
        // Clear debounce after 5 seconds
        setTimeout(() => {
          delete window[debounceKey];
        }, 5000);
      });
    }
  }

  // Helper function to get image URLs - ONLY from Firebase Storage via backend API
  // IMPORTANT: This function should ONLY retrieve images, NOT trigger generation
  // Generation is handled separately in useEffect to prevent render-cycle API spam
  const getImageUrl = (text, optionLetter = '') => {
    if (!text || !currentQuestion) return null;
    
    const imageKey = `${currentQuestion.question}_${text}`;
    
    // ONLY use dynamicImages from backend API (Firebase Storage URLs)
    if (dynamicImages[imageKey]) {
      const url = dynamicImages[imageKey];
      // Ensure it's a valid URL (Firebase Storage URL)
      if (url && (url.startsWith('http') || url.startsWith('https'))) {
        return url;
      }
    }
    
    // Log miss for debugging
    if (Object.keys(dynamicImages).length > 0 && Object.keys(dynamicImages).length <= 5) {
    }
    
    // DON'T trigger generation here - it causes API spam
    // Generation is handled in the useEffect below
    return null;
  };

  // Function to get construction type specific house model
  const get3DHouseModel = () => {
    // Construction type specific images from public/images/project-types/
    const constructionTypeImages = {
      'single-family': '/images/project-types/single-family.png',
      'multi-family': '/images/project-types/multi-family.png',
      'home-improvements': '/images/project-types/home-improvements.png',
      'adu': '/images/project-types/home-improvements.png', // Using home-improvements as fallback
      'kitchen-upgrade': '/images/project-types/home-improvements.png', // Using home-improvements as fallback
      'bathroom-upgrade': '/images/project-types/home-improvements.png' // Using home-improvements as fallback
    };
    
    // Get the construction type from project data
    const constructionType = projectData?.constructionType;
    
    // Return construction type specific image if available, otherwise fallback
    if (constructionType && constructionTypeImages[constructionType]) {
      return constructionTypeImages[constructionType];
    }
    
    // Fallback to single-family if construction type not found
    return constructionTypeImages['single-family'];
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-white" key={`question-${questionNumber}-${forceUpdateKey}-${currentQuestion?.question?.substring(0, 20)}-${currentQuestion?._forceUpdate || 0}`}>
      {/* Left Panel - Questions (60% on desktop, full width on mobile) */}
      <div className="w-full lg:w-3/5 min-h-screen lg:h-full overflow-y-auto" style={{ backgroundColor: '#f2ebe4' }}>
        <div className="p-4 sm:p-6 lg:p-8 pb-20 lg:pb-24">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 lg:mb-6 gap-3 sm:gap-0">
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
              Project Requirements
            </h1>
            <button
              onClick={handleRetake}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 bg-white px-3 py-1 rounded-md shadow-sm text-sm"
            >
              <RotateCcw size={16} />
              <span>Retake</span>
            </button>
          </div>

          {/* Question Counter */}
          <div className="mb-4 xl:mb-8">
            <p className="text-xs sm:text-sm text-gray-600 font-medium">10 Questions</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-400 rounded-md">
              <div className="flex">
                <div className="ml-3">
                  <p className="text-sm text-blue-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Current Question */}
          <div className="mb-8 xl:mb-12" key={`question-${questionNumber}-${forceUpdateKey}-${currentQuestion?.question?.substring(0, 10)}`}>
            <h2 className="text-lg sm:text-xl font-medium text-gray-900 mb-4 xl:mb-8 leading-tight">
              Question {questionNumber}. {currentQuestion.question}
            </h2>

            {isNumericQuestion ? (
              // Numeric input
              <div className="max-w-md mb-8">
                <input
                  type="number"
                  value={selectedAnswer}
                  onChange={(e) => setSelectedAnswer(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your answer"
                />
              </div>
            ) : (
              // Multiple choice options with images (responsive grid)
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8 lg:mb-12">
                {['a', 'b', 'c', 'd', 'e', 'f'].map((option) => {
                  const optionText = currentQuestion[`option_${option}`];
                  if (!optionText) return null;
                  
                  // Use the full option text for better image matching
                  const searchText = optionText;
                  
                  return (
                    <div
                      key={option}
                      className={`cursor-pointer transition-all duration-200 ${
                        selectedAnswer === option ? 'transform scale-105' : ''
                      }`}
                      onClick={() => handleAnswerSelect(option)}
                    >
                      {/* Radio Button */}
                      <div className="flex justify-center mb-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          selectedAnswer === option 
                            ? 'border-gray-800 bg-gray-800' 
                            : 'border-gray-400'
                        }`}>
                          {selectedAnswer === option && (
                            <div className="w-2 h-2 bg-white rounded-full"></div>
                          )}
                        </div>
                      </div>
                      
                      {/* Image */}
                      <div className="w-full h-24 sm:h-28 lg:h-32 bg-gray-200 rounded-lg mb-3 overflow-hidden shadow-sm">
                        {(() => {
                          const geminiImageUrl = getImageUrl(searchText, option);
                          
                          // If we have a Gemini image, show it
                          if (geminiImageUrl) {
                            return (
                              <img 
                                src={geminiImageUrl}
                                alt={searchText}
                                className="w-full h-full object-cover transition-all duration-500 hover:scale-105"
                                onError={(e) => {
                                  // Log WHY image failed to load
                                  console.error(`❌ IMAGE LOAD FAILED for "${searchText.substring(0, 20)}..."`);
                                  console.error(`   URL: ${geminiImageUrl.substring(0, 100)}...`);
                                  console.error(`   Error: ${e.type}`);
                                  console.error(`   Image natural width: ${e.target.naturalWidth}`);
                                  console.error(`   Image natural height: ${e.target.naturalHeight}`);
                                  
                                  // If Gemini image fails to load, show solid color fallback
                                  const colors = ['#dc2626', '#d97706', '#059669', '#6366f1', '#7c3aed', '#db2777'];
                                  const colorIndex = option.charCodeAt(0) % colors.length;
                                  const color = colors[colorIndex];
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center text-white font-bold text-2xl transition-all duration-500" style="background-color: ${color}">
                                      ${option}
                                    </div>
                                  `;
                                }}
                                onLoad={(e) => {
                                  // Log successful image loads
                                }}
                              />
                            );
                          }
                          
                          // No Gemini image found, show solid color fallback immediately
                          const colors = ['#dc2626', '#d97706', '#059669', '#6366f1', '#7c3aed', '#db2777'];
                          const colorIndex = option.charCodeAt(0) % colors.length;
                          const color = colors[colorIndex];
                          
                          return (
                            <div 
                              className="w-full h-full flex items-center justify-center text-white font-bold text-2xl"
                              style={{ backgroundColor: color }}
                            >
                              {option}
                            </div>
                          );
                        })()}
                      </div>
                      
                      {/* Label */}
                      <div className="text-center px-1">
                        <span className="text-xs sm:text-sm font-medium text-gray-900 leading-tight">
                          {searchText.split('(')[0].trim()}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation - Fixed at bottom on desktop, relative on mobile */}
          <div className="lg:fixed lg:bottom-8 lg:left-8 lg:right-2/5 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 sm:gap-4 mt-6 lg:mt-0 lg:pr-4">
            {/* Back Button */}
            <button
              onClick={handleBack}
              disabled={loading}
              className="bg-gray-500 hover:bg-gray-600 text-white px-4 sm:px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <ArrowLeft size={16} />
              <span>{questionNumber <= 1 ? 'Back to Form' : 'Previous'}</span>
            </button>

            {/* Spacer to ensure proper gap on desktop */}
            <div className="hidden sm:flex flex-1 min-w-8"></div>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={!selectedAnswer || loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 sm:px-8 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 text-sm sm:text-base"
            >
              <span>{loading ? 'Loading...' : (questionNumber >= 10 ? 'Get Estimate' : 'Next')}</span>
              {!loading && <ArrowRight size={16} />}
            </button>
          </div>
        </div>
      </div>

      {/* Right Panel - Estimate (40% on desktop, full width on mobile) */}
      <div className="w-full lg:w-2/5 lg:min-h-screen lg:h-full overflow-y-auto lg:sticky lg:top-0" style={{ backgroundColor: 'rgb(253, 249, 247)' }}>
        <div className="p-4 sm:p-6 lg:p-8">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 mb-4 lg:mb-8">Estimate</h2>
          
          {/* Construction Type Image */}
          <div className="flex justify-center mb-4 lg:mb-8">
            <img 
              src={get3DHouseModel()}
              alt="Construction Type"
              className="w-40 h-28 lg:w-48 lg:h-32 object-contain transition-all duration-500"
              onError={(e) => {
                // Fallback to default construction image
                e.target.src = '/images/project-types/single-family.png';
              }}
            />
          </div>

          {/* Cost Estimate Display */}
          <EstimateDisplay 
            costEstimate={costEstimate} 
            questionNumber={questionNumber}
            className="mb-6" 
          />


          {/* Action Buttons */}
          <div className="space-y-4">
            <button 
              onClick={handleShare}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-4 px-4 rounded-lg font-medium transition-colors duration-200 flex items-center justify-center space-x-2"
            >
              <Share2 size={18} />
              <span>Share</span>
            </button>
            <div className="text-center">
              <button 
                className="text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
                style={{ color: '#18735e' }}
                onMouseEnter={(e) => e.target.style.color = '#0f5a47'}
                onMouseLeave={(e) => e.target.style.color = '#18735e'}
              >
                <ExternalLink size={16} />
                <span>For more detailed estimation, click here</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionFlow;
